import { useQuery } from "@tanstack/react-query";
import AppRoutes from "AppRoutes";
import { DashboardFilterModel } from "Dashboard/models/DashboardFilterModel";
import {
  formatUrlParams,
  getBooleanFromUrl,
  getDefaultStartDate,
  getStartDateFromUrl,
  getStringFromUrl,
  tryFormatDate,
  urlDateFormat,
} from "helpers";
import { createLogger } from "logging";
import { useApi } from "providers";
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";

import { DashboardContext } from "./DashboardContext";

const logger = createLogger("DashboardProvider");

const defaultStartDate = getDefaultStartDate();
const defaultStartDateFormatted = tryFormatDate(defaultStartDate, urlDateFormat);

const getDefaultQuery = (): DashboardFilterModel => {
  return {
    keywords: "",
    startDate: defaultStartDate,
    showAgendaItems: false,
  };
};

function getUrlState(filters: DashboardFilterModel) {
  const startDateFormatted = tryFormatDate(filters.startDate, urlDateFormat);
  const hasStartDate = startDateFormatted !== defaultStartDateFormatted;
  const o: Record<string, string | undefined> = {};
  if (filters.keywords) o.q = filters.keywords;
  if (hasStartDate) o.startDate = startDateFormatted;
  if (filters.showAgendaItems) o.showAgendaItems = "1";

  const params = formatUrlParams(o);
  logger.debug(`getUrlState(): filters:=${JSON.stringify(filters)} -> '${params}'`);

  return [params, o];
}

function setUrlState(filters: DashboardFilterModel) {
  let [url, o] = getUrlState(filters);
  url = url ? `?${url}` : window.location.pathname;
  logger.debug("setUrlState(): newUrl:=", url);
  window.history.replaceState(o, "", url);
}

export const DashboardProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const { dashboardApi } = useApi();

  // Get initial values from URL
  const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);

  // Initialize unified query state from URL
  const [query, setQuery] = useState<DashboardFilterModel>(() => ({
    keywords: getStringFromUrl(urlParams, "q", ""),
    startDate: getStartDateFromUrl(urlParams, defaultStartDate),
    showAgendaItems: getBooleanFromUrl(urlParams, "showAgendaItems"),
  }));

  const resetQuery = useCallback(() => {
    setQuery(getDefaultQuery());
  }, []);

  // Listen for external URL changes (browser back/forward, manual URL edits)
  useEffect(() => {
    const handlePopState = () => {
      const urlParams = new URLSearchParams(window.location.search);
      setQuery({
        keywords: getStringFromUrl(urlParams, "q", ""),
        startDate: getStartDateFromUrl(urlParams, defaultStartDate),
        showAgendaItems: getBooleanFromUrl(urlParams, "showAgendaItems"),
      });
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    setUrlState(query);
  }, [query]);

  // Load dashboard data
  const queryResult = useQuery({
    queryKey: [`Dashboard|${JSON.stringify(query)}`],
    queryFn: async () => {
      const data = await dashboardApi.getBoards(query);
      logger.trace("Loaded", data);
      return data;
    },
  });

  const results = queryResult.data;
  const isLoading = queryResult.isFetching || !results;

  const getMeetingsUrl = useCallback(
    (boardName: string) => {
      let url = AppRoutes.toBoardMeetings(boardName);
      if (query.startDate) {
        let urlStartDate = tryFormatDate(query.startDate, urlDateFormat);
        urlStartDate = encodeURIComponent(`${urlStartDate}`);
        if (urlStartDate) {
          url += `?startDate=${urlStartDate}`;
        }
      }
      return url;
    },
    [query.startDate]
  );

  const value = useMemo(
    () => ({
      query,
      setQuery,
      resetQuery,
      isLoading,
      results,
      getMeetingsUrl,
    }),
    [query, setQuery, resetQuery, isLoading, results, getMeetingsUrl]
  );

  return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>;
};
